// src/pages/student/StudentDashboard.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import API from '../../api';
import {
    fetchStudentOverview,
    fetchStudentUpcoming,
    fetchStudentAttendanceReport,
    fetchStudentEnrollments,
} from '../../api/studentPortal';
import { usePortalStudentId } from '../../hooks/usePortalStudentId';
import ParentPortalHeader from '../../components/parent/ParentPortalHeader';
import { useParentChild } from '../../context/ParentChildContext';
import { useCurrentUser } from '../../context/UserContext';
import SessionDateTimeDisplay from '../../components/common/SessionDateTimeDisplay';
import SessionJoinAction from '../../components/classroom/SessionJoinAction';
import { formatDate, formatTime } from "../../utils/date";
import { buildSessionDateTimeParts } from '../../utils/sessionDateTimeDisplay';
import UserLink from "../../components/common/UserLink";
import WalletSummary from '../../components/WalletSummary';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import { localizeBatchName } from '../../utils/displayNames';
import {
    ChartPanel,
    CompositionDonut,
    ScoreGauge,
    CHART_COLORS,
} from '../../components/charts';

const round2 = (n) => Math.round((n ?? 0) * 100) / 100;

const StudentDashboard = () => {
    const { t } = useTranslation();
    const { user } = useCurrentUser();
    const childStudentId = usePortalStudentId();
    const { selectedChild } = useParentChild();
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [partialHint, setPartialHint] = useState('');
    const [studentId, setStudentId] = useState(null);
    const [kpis, setKpis] = useState({
        activeEnrollments: null,
        attendanceRate: null,
        upcoming7d: null,
        lastAttended: null,
        lastAttendedEnd: null,
    });
    const [upcoming, setUpcoming] = useState([]);
    const [recent, setRecent] = useState([]);
    const [upcomingOk, setUpcomingOk] = useState(false);
    const [recentOk, setRecentOk] = useState(false);

    const childOverviewTitle = useMemo(
        () => t('pages.studentDashboard.childOverview', {
            name: selectedChild?.studentName || t('pages.studentDashboard.childFallback'),
        }),
        [selectedChild?.studentName, t],
    );

    useEffect(() => {
        let aborted = false;
        (async () => {
            setLoading(true);
            setErr('');
            setPartialHint('');
            try {
                if (childStudentId) {
                    setStudentId(childStudentId);
                } else {
                    const profileRes = await API.get('/me');
                    const userId = profileRes?.data?.userId || profileRes?.data?.id;
                    setStudentId(userId);
                }

                const calls = await Promise.allSettled([
                    fetchStudentOverview(childStudentId),
                    fetchStudentUpcoming(childStudentId, 7),
                    fetchStudentAttendanceReport(childStudentId, { params: { limit: 10 } }),
                    fetchStudentEnrollments(childStudentId),
                ]);

                if (aborted) return;

                const allFailed = calls.every((c) => c.status === 'rejected');
                if (allFailed) {
                    setErr(t('pages.studentDashboard.loadFailed'));
                    return;
                }

                const ovOk = calls[0].status === 'fulfilled';
                const upOk = calls[1].status === 'fulfilled';
                const attOk = calls[2].status === 'fulfilled';
                const enrOk = calls[3].status === 'fulfilled';
                const ovData = ovOk ? calls[0].value : null;
                const upData = upOk ? calls[1].value : [];
                const attData = attOk ? calls[2].value : [];
                const enrData = enrOk ? calls[3].value : [];

                if (!ovOk || !upOk || !attOk || !enrOk) {
                    setPartialHint(t('pages.studentDashboard.partialLoad', {
                        defaultValue: 'Some dashboard data could not be loaded. Figures may be incomplete — refresh to try again.',
                    }));
                }

                const activeEnrollments = enrOk && Array.isArray(enrData) ? enrData.length : null;

                let attendanceRate = null;
                let lastAttended = null;
                let lastAttendedEnd = null;
                if (attOk && Array.isArray(attData)) {
                    const rows = attData;
                    const total = rows.length;
                    const good = rows.filter((r) =>
                        ['PRESENT'].includes(String(r.attendanceType || r.status || '').toUpperCase())
                    ).length;
                    attendanceRate = total ? round2(good / total * 100) : 0;
                    const attended = rows
                        .filter((r) => ['PRESENT'].includes(String(r.attendanceType || r.status || '').toUpperCase()))
                        .map((r) => ({
                            start: r.startTime || r.sessionStart || r.markedAt,
                            end: r.endTime || r.sessionEnd || null,
                        }))
                        .filter((r) => r.start)
                        .sort((a, b) => new Date(b.start) - new Date(a.start));
                    lastAttended = attended[0]?.start || null;
                    lastAttendedEnd = attended[0]?.end || null;
                    setRecent(rows.slice(0, 10));
                    setRecentOk(true);
                } else {
                    setRecent([]);
                    setRecentOk(false);
                }

                if (upOk && Array.isArray(upData)) {
                    setUpcoming(upData);
                    setUpcomingOk(true);
                } else {
                    setUpcoming([]);
                    setUpcomingOk(false);
                }
                const upcoming7d = upOk && Array.isArray(upData) ? upData.length : null;

                if (ovOk && ovData && typeof ovData === 'object') {
                    const o = ovData;
                    setKpis({
                        activeEnrollments: o.activeEnrollments ?? activeEnrollments,
                        attendanceRate: o.attendanceRate != null ? round2(o.attendanceRate) : attendanceRate,
                        upcoming7d: o.upcomingSessions7d ?? upcoming7d,
                        lastAttended: o.lastAttended ?? lastAttended,
                        lastAttendedEnd: o.lastAttendedEnd ?? lastAttendedEnd,
                    });
                } else {
                    setKpis({
                        activeEnrollments,
                        attendanceRate,
                        upcoming7d,
                        lastAttended,
                        lastAttendedEnd,
                    });
                }
            } catch (e) {
                setErr(t('pages.studentDashboard.loadFailed'));
            } finally {
                if (!aborted) setLoading(false);
            }
        })();
        return () => { aborted = true; };
    }, [childStudentId, t]);

    // Keep Join / Not started in sync when coach starts the class (no full-page reload).
    useEffect(() => {
        if (loading || err) return undefined;
        const timer = window.setInterval(() => {
            fetchStudentUpcoming(childStudentId, 7)
                .then((upData) => {
                    if (Array.isArray(upData)) {
                        setUpcoming(upData);
                        setUpcomingOk(true);
                        setKpis((prev) => ({ ...prev, upcoming7d: upData.length }));
                    }
                })
                .catch(() => { /* keep last good snapshot */ });
        }, 15000);
        return () => window.clearInterval(timer);
    }, [childStudentId, loading, err]);

    const lastAttendedDisplay = kpis.lastAttended ? (() => {
        const parts = buildSessionDateTimeParts(kpis.lastAttended, kpis.lastAttendedEnd);
        if (parts) {
            return (
                <>
                    <div className="text-2xl font-bold leading-tight tabular-nums">{parts.date}</div>
                    {parts.timeRange ? (
                        <div className="text-sm opacity-90 mt-0.5 tabular-nums line-clamp-1">{parts.timeRange}</div>
                    ) : null}
                </>
            );
        }
        return (
            <>
                <div className="text-2xl font-bold leading-tight tabular-nums">{formatDate(kpis.lastAttended)}</div>
                <div className="text-sm opacity-90 mt-0.5 tabular-nums line-clamp-1">{formatTime(kpis.lastAttended)}</div>
            </>
        );
    })() : '—';

    const sessionWhenCell = (row) => (
        <SessionDateTimeDisplay
            startTime={row.startTime || row.sessionStart || row.markedAt}
            endTime={row.endTime || row.sessionEnd}
        />
    );

    const MetricCard = ({ title, value, icon, gradient, suffix = '', valueClassName = 'text-3xl font-bold' }) => (
        <div className={`bg-gradient-to-br ${gradient} rounded-2xl shadow-lg p-5 md:p-6 text-white h-full`}>
            <div className="flex h-full items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="text-sm opacity-90 mb-1">{title}</div>
                    <div className={`${valueClassName} h-16 flex flex-col justify-center`}>
                        {loading ? '...' : (React.isValidElement(value) ? value : (value == null || value === '' ? '—' : `${value}${suffix}`))}
                    </div>
                </div>
                <div className="w-12 h-12 shrink-0 bg-white/20 rounded-full flex items-center justify-center">
                    {icon}
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <PageLoadingShell
                variant="page-shell"
                title={childStudentId ? childOverviewTitle : t('nav.dashboard')}
                subtitle={childStudentId
                    ? t('pages.studentDashboard.childSubtitle')
                    : t('pages.studentDashboard.subtitle')}
                showBack={false}
            />
        );
    }

    if (err) return (
        <div className="page-shell">
            <div className="max-w-2xl mx-auto mt-20 bg-red-50 border border-red-200 rounded-xl p-6 text-red-600">
                {err}
            </div>
        </div>
    );

    return (
        <div className="page-shell">
            <ParentPortalHeader />

            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                    {childStudentId
                        ? childOverviewTitle
                        : t('dashboard.welcome', { name: user?.firstName || t('dashboard.defaultStudent') })}
                </h1>
                <p className="text-gray-600 mt-2">
                    {childStudentId
                        ? t('pages.studentDashboard.childSubtitle')
                        : t('pages.studentDashboard.subtitle')}
                </p>
            </div>

            {partialHint && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
                    {partialHint}
                </div>
            )}

            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 items-stretch">
                <MetricCard
                    title={t('pages.studentDashboard.activeEnrollments')}
                    value={kpis.activeEnrollments}
                    gradient="from-blue-500 to-blue-600"
                    icon={
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    }
                />
                <MetricCard
                    title={t('pages.studentDashboard.attendanceRate')}
                    value={kpis.attendanceRate == null ? null : round2(kpis.attendanceRate)}
                    suffix={kpis.attendanceRate == null ? '' : '%'}
                    gradient="from-green-500 to-emerald-600"
                    icon={
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    }
                />
                <MetricCard
                    title={t('pages.studentDashboard.upcoming7d')}
                    value={kpis.upcoming7d}
                    gradient="from-purple-500 to-purple-600"
                    icon={
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    }
                />
                <MetricCard
                    title={t('pages.studentDashboard.lastAttended')}
                    value={lastAttendedDisplay}
                    gradient="from-orange-500 to-orange-600"
                    icon={
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    }
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ChartPanel
                    title={t('charts.attendanceSnapshot', { defaultValue: 'Attendance snapshot' })}
                    actionTo={childStudentId ? undefined : '/student/attendance'}
                    actionLabel={childStudentId ? undefined : t('charts.viewHistory', { defaultValue: 'History' })}
                    empty={(kpis.attendanceRate == null || Number(kpis.attendanceRate) === 0) && (!recent || recent.length === 0)}
                    emptyMessage={t('charts.noAttendanceYet', { defaultValue: 'No attendance recorded yet' })}
                    height={200}
                >
                    <ScoreGauge
                        value={kpis.attendanceRate ?? 0}
                        label={t('pages.studentDashboard.attendanceRate')}
                        color={CHART_COLORS.success}
                    />
                </ChartPanel>
                <ChartPanel
                    title={t('charts.recentMarks', { defaultValue: 'Recent marks' })}
                    empty={!recent || recent.length === 0}
                    emptyMessage={t('charts.noRecentMarks', { defaultValue: 'No recent attendance marks' })}
                    height={220}
                >
                    <CompositionDonut
                        data={[
                            {
                                name: t('reports.common.present', { defaultValue: 'Present' }),
                                value: (recent || []).filter((r) =>
                                    ['PRESENT'].includes(String(r.attendanceType || r.status || '').toUpperCase()),
                                ).length,
                                color: CHART_COLORS.success,
                            },
                            {
                                name: t('reports.common.absent', { defaultValue: 'Absent' }),
                                value: (recent || []).filter((r) =>
                                    !['PRESENT'].includes(String(r.attendanceType || r.status || '').toUpperCase()),
                                ).length,
                                color: CHART_COLORS.danger,
                            },
                        ]}
                        chartSize={180}
                        centerValue={kpis.attendanceRate == null ? '—' : `${round2(kpis.attendanceRate)}%`}
                        centerLabel={t('charts.rate', { defaultValue: 'Rate' })}
                    />
                </ChartPanel>
            </div>

            <div>
                <WalletSummary
                    studentId={studentId}
                    studentPortalMode
                    childStudentId={childStudentId}
                    previewLimit={5}
                />
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900">{t('pages.studentDashboard.upcomingSessions')}</h2>
                </div>
                <div className="p-6">
                    {(upcoming || []).length === 0 ? (
                        <div className="text-center py-12">
                            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p className="text-gray-500">
                                {upcomingOk
                                    ? t('pages.studentDashboard.noUpcomingSessions')
                                    : t('pages.studentDashboard.partialLoad', {
                                        defaultValue: 'Some dashboard data could not be loaded. Figures may be incomplete — refresh to try again.',
                                    })}
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm text-center">
                                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                                    <tr>
                                        <th className="px-4 py-3 font-semibold text-gray-700">{t('table.when')}</th>
                                        <th className="px-4 py-3 font-semibold text-gray-700">{t('table.level')}</th>
                                        <th className="px-4 py-3 font-semibold text-gray-700">{t('table.batch')}</th>
                                        <th className="px-4 py-3 font-semibold text-gray-700">{t('table.coach')}</th>
                                    <th className="px-4 py-3 font-semibold text-gray-700">{t('classroom.live.joinSession', { defaultValue: 'Join Session' })}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(upcoming || []).map((s, i) => (
                                        <tr key={String(s.sessionId ?? i)} className="border-t border-gray-100 hover:bg-green-50 transition-colors">
                                            <td className="px-4 py-3">{sessionWhenCell(s)}</td>
                                            <td className="px-4 py-3">{s.levelName || s.level || '—'}</td>
                                            <td className="px-4 py-3">{localizeBatchName(s.batchName || s.batch, t) || '—'}</td>
                                            <td className="px-4 py-3">
                                                {s.coachId && s.coachName ? (
                                                    <UserLink id={s.coachId} name={s.coachName} />
                                                ) : (
                                                    s.coachName || '—'
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <SessionJoinAction
                                                    sessionId={s.sessionId}
                                                    role={childStudentId ? 'PARENT' : 'STUDENT'}
                                                    classroomEligible={s.classroomEligible}
                                                    canJoin={s.canJoin}
                                                    meetingStatus={s.meetingStatus}
                                                    status={s.status}
                                                    endTime={s.endTime}
                                                    studentId={childStudentId || studentId}
                                                    compact
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900">{t('pages.studentDashboard.recentAttendance')}</h2>
                </div>
                <div className="p-6">
                    {(recent || []).length === 0 ? (
                        <div className="text-center py-12">
                            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            <p className="text-gray-500">
                                {recentOk
                                    ? t('pages.studentDashboard.noAttendanceRecords')
                                    : t('pages.studentDashboard.partialLoad', {
                                        defaultValue: 'Some dashboard data could not be loaded. Figures may be incomplete — refresh to try again.',
                                    })}
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm text-center">
                                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                                    <tr>
                                        <th className="px-4 py-3 font-semibold text-gray-700">{t('table.when')}</th>
                                        <th className="px-4 py-3 font-semibold text-gray-700">{t('common.status')}</th>
                                        <th className="px-4 py-3 font-semibold text-gray-700">{t('table.level')}</th>
                                        <th className="px-4 py-3 font-semibold text-gray-700">{t('table.batch')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(recent || []).map((r, i) => {
                                        const status = r.attendanceType || r.status || '—';
                                        const statusColor =
                                            status.toUpperCase() === 'PRESENT' ? 'text-green-700 bg-green-100' :
                                                status.toUpperCase() === 'ABSENT' ? 'text-red-700 bg-red-100' :
                                                    'text-gray-700 bg-gray-100';

                                        return (
                                            <tr key={i} className="border-t border-gray-100 hover:bg-blue-50 transition-colors">
                                                <td className="px-4 py-3">{sessionWhenCell(r)}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                                                        {status.toUpperCase() === 'PRESENT'
                                                            ? t('reports.common.present')
                                                            : status.toUpperCase() === 'ABSENT'
                                                                ? t('reports.common.absent')
                                                                : status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">{r.levelName || '—'}</td>
                                                <td className="px-4 py-3">{localizeBatchName(r.batchName, t) || '—'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StudentDashboard;
